// SeoManagement.jsx — landing for the SEO Management package.
// Lives at /seo-management/:tab? — deep-linkable like EmailManagement.
//
// Phase 2 wired here:
//   - "+ New X" form per tab (keyword / brief / article / faq)
//   - "Generate" buttons that fire the /request endpoints, kicking off
//     the worker-side LLM processors
//   - Viewer modal per entity showing the AI output: brief outline,
//     article markdown preview, faq draft answers
//   - Status badges + auto-poll while any row is in 'generating' /
//     'reviewing' so the UI reflects the worker's progress without a refresh

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { get, post, putGeneral } from "../../api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import SystemFilterBar from "components/SystemFilterBar";
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from "../../helpers/systemFilters";
import useBrandList from "../../hooks/useBrandList";

const TABS = [
  { key: 'keywords', label: 'Keywords', endpoint: '/api/seo/keywords', perm: 'SEO_KEYWORD_VIEW' },
  { key: 'briefs',   label: 'Briefs',   endpoint: '/api/seo/briefs',   perm: 'SEO_BRIEF_VIEW' },
  { key: 'articles', label: 'Articles', endpoint: '/api/seo/articles', perm: 'SEO_ARTICLE_VIEW' },
  { key: 'faqs',     label: 'FAQ Q&A',  endpoint: '/api/seo/faqs',     perm: 'SEO_FAQ_VIEW' },
];

const COLUMNS = {
  keywords: [['keyword', 'Keyword'], ['intent', 'Intent'], ['searchVolume', 'Vol.'], ['difficultyScore', 'Diff.'], ['topicCluster', 'Cluster']],
  briefs:   [['title', 'Title'], ['primaryKeyword', 'Primary KW'], ['targetWordCount', 'Words'], ['status', 'Status'], ['updatedAt', 'Updated']],
  articles: [['title', 'Title'], ['status', 'Status'], ['wordCount', 'Words'], ['primaryKeyword', 'Primary KW'], ['aiCostUSD', 'AI $']],
  faqs:     [['question', 'Question'], ['source', 'Source'], ['primaryKeyword', 'Primary KW'], ['answers.length', 'Drafts'], ['hasPublishedAnswer', 'Published']],
};

function getPath(obj, path) { return path.split('.').reduce((o, k) => (o == null ? null : o[k]), obj); }
function fmtCell(v, key) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (key === 'aiCostUSD') return `$${Number(v).toFixed(4)}`;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) { try { return new Date(v).toLocaleString(); } catch {} }
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 60) + '…';
  return String(v);
}

function statusBadge(status) {
  const map = {
    draft:      { bg: '#eef2ff', fg: '#15172b' },
    reviewing:  { bg: '#fff4cc', fg: '#856404' },
    reviewed:   { bg: '#e7f5ec', fg: '#1e6b3e' },
    approved:   { bg: '#e7f5ec', fg: '#1e6b3e' },
    published:  { bg: '#1e6b3e', fg: '#fff' },
    scheduled:  { bg: '#eef2ff', fg: '#15172b' },
    archived:   { bg: '#fafbfc', fg: '#888' },
    used:       { bg: '#eef2ff', fg: '#15172b' },
  };
  const s = map[status] || { bg: '#fafbfc', fg: '#525252' };
  return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, background: s.bg, color: s.fg }}>{status || '—'}</span>;
}

// ────────────────────────────────────────────────────────────── Forms
function CreateForm({ tab, onClose, onCreated }) {
  const [form, setForm] = useState(initialFor(tab));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const endpoint = TABS.find((t) => t.key === tab).endpoint;
      await post(endpoint, form, false);
      onCreated && onCreated();
    } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`New ${labelFor(tab, 'singular')}`} onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {fieldsFor(tab).map((f) => (
        <label key={f.key} style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          {f.label}{f.required && ' *'}
          {f.type === 'select' ? (
            <select className="form-control" value={form[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}>
              {(f.options || []).map((o) => (<option key={o.value || o} value={o.value || o}>{o.label || o}</option>))}
            </select>
          ) : f.type === 'textarea' ? (
            <textarea rows={f.rows || 3} className="form-control" value={form[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />
          ) : (
            <input type={f.type || 'text'} className="form-control" value={form[f.key] ?? ''} onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} />
          )}
          {f.help && <div style={{ fontSize: 11, color: '#888' }}>{f.help}</div>}
        </label>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
      </div>
    </Modal>
  );
}

function initialFor(tab) {
  switch (tab) {
    case 'keywords': return { keyword: '', intent: 'unknown', searchVolume: '', difficultyScore: '', topicCluster: '' };
    case 'briefs':   return { title: '', primaryKeyword: '', targetIntent: 'informational', targetWordCount: 1500 };
    case 'articles': return { title: '', primaryKeyword: '', brief_id: '' };
    case 'faqs':     return { question: '', primaryKeyword: '', source: 'manual', sourceUrl: '' };
    default: return {};
  }
}

function fieldsFor(tab) {
  switch (tab) {
    case 'keywords': return [
      { key: 'keyword', label: 'Keyword', required: true, placeholder: 'best running shoes' },
      { key: 'intent', label: 'Intent', type: 'select', options: ['unknown', 'informational', 'navigational', 'transactional', 'commercial'] },
      { key: 'searchVolume', label: 'Search volume (optional)', type: 'number' },
      { key: 'difficultyScore', label: 'Difficulty 0-100 (optional)', type: 'number' },
      { key: 'topicCluster', label: 'Topic cluster (optional)' },
    ];
    case 'briefs': return [
      { key: 'title', label: 'Article title', required: true, placeholder: 'How to pick running shoes' },
      { key: 'primaryKeyword', label: 'Primary keyword' },
      { key: 'targetIntent', label: 'Search intent', type: 'select', options: ['informational', 'navigational', 'transactional', 'commercial'] },
      { key: 'targetWordCount', label: 'Target word count', type: 'number', help: 'AI generator aims for this length.' },
    ];
    case 'articles': return [
      { key: 'title', label: 'Article title', required: true, placeholder: 'How to pick running shoes' },
      { key: 'primaryKeyword', label: 'Primary keyword' },
      { key: 'brief_id', label: 'Brief id (optional)', help: 'Paste a brief id to seed the AI generator with its outline.' },
    ];
    case 'faqs': return [
      { key: 'question', label: 'Question', required: true, placeholder: 'What size hammer should I buy?' },
      { key: 'primaryKeyword', label: 'Primary keyword' },
      { key: 'source', label: 'Source', type: 'select', options: ['manual', 'people-also-ask', 'quora', 'reddit', 'stack-exchange', 'gsc-search-console', 'inbox', 'other'] },
      { key: 'sourceUrl', label: 'Source URL (optional)' },
    ];
    default: return [];
  }
}

function labelFor(tab, form) {
  const map = { keywords: 'keyword', briefs: 'brief', articles: 'article', faqs: 'FAQ' };
  return map[tab] || tab;
}

// ────────────────────────────────────────────────────────────── Viewer
// Per-article publish picker. Lists configured SeoPublishTargets for
// the company (optionally scoped to the article's brand), lets the
// operator multi-select, and submits POST /publish with targetIds.
// The worker then fans out through each adapter.
function PublishPicker({ row, onClose, onChanged }) {
  const [targets, setTargets] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const url = row.brand_id ? `/api/seo/publish-targets?brand_id=${encodeURIComponent(row.brand_id)}` : '/api/seo/publish-targets';
    get(url)
      .then((res) => {
        if (!alive) return;
        const all = (res && res.data) || [];
        const enabled = all.filter((t) => t.enabled !== false);
        setTargets(enabled);
        // Pre-select everything by default — most operators want fan-out.
        setPicked(enabled.map((t) => t._id));
        setLoading(false);
      })
      .catch((e) => { if (alive) { setErr((e && e.message) || 'Load failed'); setLoading(false); } });
    return () => { alive = false; };
  }, [row.brand_id]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!picked.length) { setErr('Pick at least one target.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await post(`/api/seo/articles/${row._id}/publish`, { targetIds: picked }, false);
      if (res && res.error) throw new Error(res.error);
      onChanged && onChanged();
    } catch (e) { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Publish failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Publish "${row.title || 'Article'}"`} onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {loading ? (
        <div style={{ padding: 20, color: '#888' }}>Loading targets…</div>
      ) : targets.length === 0 ? (
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: '#525252' }}>No publish targets configured yet.</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Add a WordPress, Ghost, webhook, or custom endpoint in <a href="/seo-management/publish-targets">Publish Targets</a> first.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>Pick where to publish. The worker will push the article to each selected target and record the result on the article history.</div>
          {targets.map((t) => (
            <label key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #f1f1f1', fontSize: 13 }}>
              <input type="checkbox" checked={picked.includes(t._id)} onChange={() => toggle(t._id)} />
              <div style={{ flex: 1 }}>
                <div><strong>{t.name}</strong> <span style={{ color: '#888', fontSize: 11, marginLeft: 6 }}>{t.kind}</span></div>
                {t.lastError && <div style={{ color: '#a71d2a', fontSize: 11 }}>last error: {t.lastError.slice(0, 80)}</div>}
              </div>
            </label>
          ))}
        </>
      )}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy || !picked.length}>{busy ? 'Queuing…' : `Publish to ${picked.length} target${picked.length === 1 ? '' : 's'}`}</button>
      </div>
    </Modal>
  );
}

function PublishHistoryStrip({ row }) {
  const h = Array.isArray(row.publishHistory) ? row.publishHistory : [];
  if (!h.length) return null;
  return (
    <div style={{ marginTop: 14, padding: 10, background: '#fafbfc', borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>Publish history</div>
      {h.slice(-10).reverse().map((e, idx) => (
        <div key={idx} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: e.status === 'success' ? '#1e6b3e' : '#a71d2a', display: 'inline-block' }} />
          <span style={{ minWidth: 90 }}>{e.targetName || e.targetKind}</span>
          {e.status === 'success' && e.externalUrl ? <a href={e.externalUrl} target="_blank" rel="noopener noreferrer">{e.externalUrl}</a> : null}
          {e.status === 'failed' && <span style={{ color: '#a71d2a' }}>{(e.error || '').slice(0, 90)}</span>}
          <span style={{ color: '#888', marginLeft: 'auto' }}>{e.ts ? new Date(e.ts).toLocaleString() : ''}</span>
        </div>
      ))}
    </div>
  );
}

function ArticleViewer({ row, onClose, onChanged }) {
  const [picker, setPicker] = useState(false);
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);
  return (
    <Modal title={row.title || 'Article'} onClose={onClose} wide>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
        Status: {statusBadge(row.status)} · Words: {row.wordCount || 0}
        {isSuperAdmin && ` · AI cost: $${Number(row.aiCostUSD || 0).toFixed(4)} · ${row.llmCallCount || 0} call${(row.llmCallCount || 0) === 1 ? '' : 's'}`}
      </div>
      {(row.metaTitle || row.metaDescription) && (
        <div style={{ background: '#fafbfc', padding: 8, fontSize: 12, borderRadius: 6, marginBottom: 12 }}>
          <div><strong>Meta title:</strong> {row.metaTitle || '—'}</div>
          <div><strong>Meta description:</strong> {row.metaDescription || '—'}</div>
          <div><strong>Slug:</strong> /{row.slug || '—'}</div>
        </div>
      )}
      {row.excerpt && (
        <div style={{ fontSize: 13, color: '#525252', marginBottom: 12, fontStyle: 'italic' }}>{row.excerpt}</div>
      )}
      <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, background: '#fafbfc', padding: 12, borderRadius: 6, maxHeight: '50vh', overflow: 'auto' }}>
        {row.bodyMarkdown || <em>(not yet generated)</em>}
      </div>
      <PublishHistoryStrip row={row} />
      {['reviewing', 'approved', 'scheduled', 'draft'].includes(row.status) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="acc-btn acc-btn-primary" onClick={() => setPicker(true)}>Publish to targets…</button>
        </div>
      )}
      {picker && (
        <PublishPicker row={row} onClose={() => setPicker(false)} onChanged={() => { setPicker(false); onChanged && onChanged(); }} />
      )}
    </Modal>
  );
}

function BriefViewer({ row, onClose }) {
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);
  return (
    <Modal title={row.title || 'Brief'} onClose={onClose} wide>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
        Status: {statusBadge(row.status)} · Intent: {row.targetIntent} · Target: {row.targetWordCount || '—'} words
        {isSuperAdmin && ` · AI cost: $${Number(row.aiCostUSD || 0).toFixed(4)}`}
      </div>
      <div style={{ marginBottom: 10 }}>
        <strong style={{ fontSize: 12 }}>Primary keyword:</strong> {row.primaryKeyword || '—'}
      </div>
      {(row.secondaryKeywords || []).length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 12 }}>
          <strong>Secondary:</strong> {row.secondaryKeywords.map((k, i) => (<span key={i} style={{ background: '#eef2ff', padding: '1px 6px', borderRadius: 8, marginRight: 4 }}>{k}</span>))}
        </div>
      )}
      {(row.semanticKeywords || []).length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 12 }}>
          <strong>Semantic (LSI):</strong> {row.semanticKeywords.join(', ')}
        </div>
      )}
      {(row.headings || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h6 style={{ fontSize: 13, marginBottom: 6 }}>Outline</h6>
          <ul style={{ fontSize: 12 }}>
            {row.headings.map((h, i) => (
              <li key={i} style={{ marginLeft: h.level === 'h3' ? 18 : 0 }}>
                <strong>[{h.level}]</strong> {h.text}
                {h.notes && <span style={{ color: '#888' }}> — {h.notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(row.internalLinkSuggestions || []).length > 0 && (
        <div style={{ fontSize: 12 }}>
          <strong>Internal links:</strong>
          <ul>{row.internalLinkSuggestions.map((l, i) => (<li key={i}>{l.label} ({l.url})</li>))}</ul>
        </div>
      )}
    </Modal>
  );
}

function FaqViewer({ row, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);

  const promote = async (idx) => {
    if (!window.confirm('Promote this draft to published?')) return;
    setBusy(true);
    try {
      // Update each answer: target idx → published; everything else → keep
      const answers = (row.answers || []).map((a, i) => ({ ...a, status: i === idx ? 'published' : (a.status === 'published' ? 'approved' : a.status) }));
      await putGeneral(`/api/seo/faqs/${row._id}`, { answers, hasPublishedAnswer: true });
      onChanged && onChanged();
    } catch (e) { alert((e && e.message) || 'Promote failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={row.question || 'FAQ'} onClose={onClose} wide>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
        Source: {row.source} · Drafts: {(row.answers || []).length}
        {isSuperAdmin && ` · AI cost: $${Number(row.aiCostUSD || 0).toFixed(4)}`}
      </div>
      {(row.answers || []).length === 0 && (
        <div style={{ background: '#fafbfc', padding: 16, fontSize: 12, color: '#888', borderRadius: 6 }}>
          No drafts yet. Click "Generate answer" on the FAQ row to fire the worker.
        </div>
      )}
      {(row.answers || []).map((a, i) => (
        <div key={i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
            {statusBadge(a.status)} · {a.generatedBy}{isSuperAdmin && ` · $${Number(a.aiCostUSD || 0).toFixed(4)}`} · {new Date(a.createdAt || Date.now()).toLocaleString()}
          </div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{a.bodyMarkdown}</div>
          {a.status === 'draft' && (
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <button className="acc-btn acc-btn-primary" style={{ fontSize: 11, padding: '2px 10px' }} onClick={() => promote(i)} disabled={busy}>Promote to Published</button>
            </div>
          )}
        </div>
      ))}
    </Modal>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: wide ? 820 : 540, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0" style={{ wordBreak: 'break-word', paddingRight: 12 }}>{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Page
export default function SeoManagement() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux   = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux     = useSelector((s) => s.user.allPermission);
  const FinalPermissions = useMemo(() => (
    (allPermissionRedux || [])
      .filter((p) => (companyPermissionRedux || []).includes(p))
      .filter((p) => (usersPermissionRedux || []).includes(p))
  ), [allPermissionRedux, companyPermissionRedux, usersPermissionRedux]);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const has = (perm) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[perm]);

  const { tab: urlTab } = useParams();
  const history = useHistory();
  const visibleTabs = TABS.filter((t) => has(t.perm));
  const tab = (TABS.find((t) => t.key === urlTab) ? urlTab : (visibleTabs[0]?.key || 'keywords'));
  const setTab = (k) => history.push(`/seo-management/${k}`);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [researching, setResearching] = useState(false);
  const [researchSeed, setResearchSeed] = useState('');
  // Header filter — brand + date (today / last week / last month / custom),
  // shared with the other system pages. SEO rows stamp createdAt/updatedAt and
  // carry brand_id, both of which applySystemFilter recognises out of the box.
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  // Brand + date filtering happens client-side over the fetched rows so the
  // header controls stay instant and consistent with the other systems.
  // Declared before any early return so the hook order is stable.
  const visibleRows = useMemo(() => applySystemFilter(rows, filters), [rows, filters]);

  const activeTab = TABS.find((t) => t.key === tab);
  const refresh = useCallback(async () => {
    if (!activeTab) return;
    setBusy(true); setErr(null);
    try {
      const r = await get(activeTab.endpoint);
      setRows(((r && r.data) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setBusy(false); }
  }, [activeTab]);

  useEffect(() => { refresh(); }, [refresh]);
  // Auto-poll while any row is in an in-flight state — the worker
  // transitions briefs draft→reviewed and articles draft→reviewing
  // asynchronously, so the UI needs to refresh without a manual click.
  useEffect(() => {
    const stale = rows.some((r) =>
      (tab === 'briefs' && r.status === 'draft') ||
      (tab === 'articles' && (r.status === 'draft' || r.status === 'reviewing'))
    );
    const interval = stale ? 5_000 : 30_000;
    const t = setInterval(() => refresh(), interval);
    return () => clearInterval(t);
  }, [refresh, rows, tab]);

  const discoverKeywords = async () => {
    const seed = (researchSeed || '').trim();
    if (!seed) return;
    setResearching(true);
    try {
      await post('/api/seo/keywords/research', { seed }, false);
      setResearchSeed('');
      // The worker drops new rows into SeoKeyword as it goes; refresh
      // every few seconds for a minute so the user sees them appear.
      let n = 0;
      const t = setInterval(() => { refresh(); if (++n > 12) clearInterval(t); }, 5000);
      setTimeout(() => clearInterval(t), 60_000);
    } catch (e) { alert((e && e.message) || 'Research failed'); }
    finally { setResearching(false); }
  };

  const generate = async (row) => {
    if (tab === 'briefs' && !has('SEO_BRIEF_REQUEST')) return;
    if (tab === 'articles' && !has('SEO_ARTICLE_REQUEST')) return;
    if (tab === 'faqs' && !has('SEO_FAQ_ANSWER_REQUEST')) return;
    const url = tab === 'faqs'
      ? `/api/seo/faqs/${row._id}/answer-request`
      : `/api/seo/${tab}/${row._id}/request`;
    try {
      await post(url, {}, false);
      setTimeout(refresh, 1000);
    } catch (e) {
      alert((e && e.message) || 'Generate failed');
    }
  };

  if (visibleTabs.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Your company doesn't have the SEO Management package enabled. Ask a super admin to add it.
        </div>
      </div>
    );
  }

  // Hide the AI-cost column for tenant admins — only super-admin
  // sees raw provider cost. (isSuperAdmin declared once at the top
  // of the component.)
  const cols = (COLUMNS[tab] || []).filter(([key]) =>
    isSuperAdmin || key !== 'aiCostUSD'
  );
  const canAdd = (
    (tab === 'keywords' && has('SEO_KEYWORD_ADD')) ||
    (tab === 'briefs'   && has('SEO_BRIEF_ADD')) ||
    (tab === 'articles' && has('SEO_ARTICLE_ADD')) ||
    (tab === 'faqs'     && has('SEO_FAQ_ADD'))
  );

  return (
    <div className="container py-4">
      <style>{`
        .sm-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 16px; }
        .sm-tab { padding: 8px 14px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 13px; color: #525252; }
        .sm-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .sm-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; }
        .sm-table { width: 100%; font-size: 13px; }
        .sm-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .sm-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; }
        .sm-empty { color: #999; padding: 16px; text-align: center; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h3 className="mb-0">SEO Management</h3>
        {canAdd && (
          <button className="acc-btn acc-btn-primary" onClick={() => setCreating(true)}>+ New {labelFor(tab, 'singular')}</button>
        )}
      </div>

      <div className="sm-tabs">
        {visibleTabs.map((t) => (
          <div key={t.key} className={"sm-tab" + (tab === t.key ? " active" : "")} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      <SystemFilterBar
        filters={filters}
        setFilters={setFilters}
        brands={brands}
        resultCount={visibleRows.length}
        totalCount={rows.length}
      />

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      {tab === 'keywords' && has('SEO_KEYWORD_ADD') && (
        <div className="sm-card" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#525252', flexShrink: 0 }}>Discover keywords:</div>
          <input
            className="form-control"
            placeholder="seed term — e.g. 'best running shoes'"
            value={researchSeed}
            onChange={(e) => setResearchSeed(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') discoverKeywords(); }}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button className="acc-btn acc-btn-primary" onClick={discoverKeywords} disabled={researching || !researchSeed.trim()}>
            {researching ? 'Working…' : 'Research'}
          </button>
          <div style={{ fontSize: 11, color: '#888' }}>Google Autocomplete + AI expansion. Free, no key.</div>
        </div>
      )}

      <div className="sm-card">
        {busy && rows.length === 0 && <div className="sm-empty">Loading…</div>}
        {!busy && rows.length === 0 && <div className="sm-empty">No {activeTab.label.toLowerCase()} yet.</div>}
        {!busy && rows.length > 0 && visibleRows.length === 0 && <div className="sm-empty">No {activeTab.label.toLowerCase()} match the current filters.</div>}
        {visibleRows.length > 0 && (
          <table className="sm-table">
            <thead><tr>{cols.map(([key, label]) => (<th key={key}>{label}</th>))}<th /></tr></thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r._id}>
                  {cols.map(([key]) => (
                    <td key={key}>
                      {key === 'status'
                        ? statusBadge(r.status)
                        : key === 'answers.length'
                        ? (r.answers || []).length
                        : fmtCell(getPath(r, key), key)}
                    </td>
                  ))}
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {tab !== 'keywords' && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginRight: 4 }} onClick={() => setViewing(r)}>View</button>
                    )}
                    {tab === 'briefs' && r.status === 'draft' && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => generate(r)}>Generate</button>
                    )}
                    {tab === 'articles' && (r.status === 'draft' || !r.bodyMarkdown) && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => generate(r)}>Generate</button>
                    )}
                    {tab === 'faqs' && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => generate(r)}>Generate answer</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <CreateForm
          tab={tab}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); refresh(); }}
        />
      )}
      {viewing && tab === 'articles' && (
        <ArticleViewer row={viewing} onClose={() => setViewing(null)} onChanged={() => { setViewing(null); refresh(); }} />
      )}
      {viewing && tab === 'briefs' && (
        <BriefViewer row={viewing} onClose={() => setViewing(null)} />
      )}
      {viewing && tab === 'faqs' && (
        <FaqViewer row={viewing} onClose={() => setViewing(null)} onChanged={() => { setViewing(null); refresh(); }} />
      )}
    </div>
  );
}
